import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  CreditCard,
  DollarSign,
  Calendar,
  Filter,
  Download,
  Eye,
  Building,
  User,
  X,
  CheckCircle,
  AlertCircle,
  Edit,
  Grid,
  List,
  BarChart3
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { paymentsAPI, roomsAPI, billingAPI } from '../services/api';
import { formatCurrency, formatDate, formatDateForInput, getMonthName } from '../utils/helpers';
import { ChevronUp, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', or 'chart'
  const [formData, setFormData] = useState({
    room_id: '',
    payment_date: formatDateForInput(new Date()),
    amount: '',
    payment_type: 'rent',
    payment_method: 'cash',
    payment_month: new Date().getMonth() + 1,
    payment_year: new Date().getFullYear(),
    notes: ''
  });
  const [filters, setFilters] = useState({
    room_id: '',
    year: new Date().getFullYear(),
    month: ''
  });
  const [roomSummaries, setRoomSummaries] = useState({});
  const [selectedRoomBill, setSelectedRoomBill] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'check', label: 'Check' },
    { value: 'mobile_money', label: 'Mobile Money' },
    { value: 'card', label: 'Card Payment' }
  ];

  useEffect(() => {
    fetchRooms();
    fetchPayments();
  }, []);

  const sortedPayments = React.useMemo(() => {
    const list = (payments || []).slice();
    if (!sortConfig.key) return list;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      switch (sortConfig.key) {
        case 'date': {
          const av = a.payment_date ? new Date(a.payment_date).getTime() : 0;
          const bv = b.payment_date ? new Date(b.payment_date).getTime() : 0;
          return (av - bv) * dir;
        }
        case 'room': {
          return String(a.room_number || '').localeCompare(String(b.room_number || ''), undefined, { numeric: true, sensitivity: 'base' }) * dir;
        }
        case 'tenant': {
          return String(a.tenant_name || '').localeCompare(String(b.tenant_name || ''), undefined, { sensitivity: 'base' }) * dir;
        }
        case 'type': {
          return String(a.payment_type || '').localeCompare(String(b.payment_type || ''), undefined, { sensitivity: 'base' }) * dir;
        }
        case 'period': {
          if (a.bill_year !== b.bill_year) return (a.bill_year - b.bill_year) * dir;
          return (a.bill_month - b.bill_month) * dir;
        }
        case 'amount': {
          return (Number(a.amount) - Number(b.amount)) * dir;
        }
        case 'method': {
          const am = (paymentMethods.find(m => m.value === a.payment_method)?.label) || a.payment_method || '';
          const bm = (paymentMethods.find(m => m.value === b.payment_method)?.label) || b.payment_method || '';
          return String(am).localeCompare(String(bm), undefined, { sensitivity: 'base' }) * dir;
        }
        default:
          return 0;
      }
    });
  }, [payments, sortConfig]);

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline-block ml-2" />
    ) : (
      <ChevronDown className="w-3 h-3 inline-block ml-2" />
    );
  };

  useEffect(() => {
    fetchPayments();
  }, [filters]);

  const fetchRooms = async () => {
    try {
      const response = await roomsAPI.getAll();
      setRooms(response.data.rooms);
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load rooms');
    }
  };

  const fetchPayments = async () => {
    try {
      const params = {};
      if (filters.room_id) params.room_id = filters.room_id;
      if (filters.year) params.year = filters.year;
      if (filters.month) params.month = filters.month;

      const response = await paymentsAPI.getAll(params);
      setPayments(response.data.payments);

      // Fetch room summaries for financial information
      const summaries = {};
      for (const room of rooms) {
        if (room.is_occupied) {
          try {
            const summaryResponse = await billingAPI.getRoomSummary(room.id);
            summaries[room.id] = summaryResponse.data;
          } catch (error) {
            console.error(`Error fetching summary for room ${room.id}:`, error);
          }
        }
      }
      setRoomSummaries(summaries);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomBillForPayment = async (roomId, month, year) => {
    try {
      const response = await billingAPI.getMonthlyBill(roomId, year, month);
      setSelectedRoomBill(response.data.bill);
    } catch (error) {
      // Bill might not exist yet, that's ok
      setSelectedRoomBill(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert and validate data before sending
      const paymentData = {
        room_id: parseInt(formData.room_id),
        payment_date: formData.payment_date,
        amount: parseFloat(formData.amount),
        payment_type: formData.payment_type,
        payment_method: formData.payment_method,
        payment_month: parseInt(formData.payment_month),
        payment_year: parseInt(formData.payment_year),
        notes: formData.notes || ''
      };

      // Validate required fields
      if (!paymentData.room_id || paymentData.room_id <= 0) {
        toast.error('Please select a valid room');
        return;
      }
      if (!paymentData.amount || paymentData.amount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }

      if (editingPayment) {
        // Update existing payment
        await paymentsAPI.update(editingPayment.id, paymentData);
        toast.success('Payment updated successfully');
        setEditingPayment(null);
      } else {
        const response = await billingAPI.recordPayment(paymentData);
        // Check for overpayment
        if (response.data?.overpayment > 0) {
          toast.success(`Payment recorded successfully! Overpayment of ${formatCurrency(response.data.overpayment)} will be credited to future bills.`);
        } else {
          toast.success('Payment recorded successfully');
        }
      }

      fetchPayments();
      closeModal();
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error(error.response?.data?.error || 'Failed to save payment');
    }
  };

  const openModal = () => {
    setFormData({
      room_id: '',
      payment_date: formatDateForInput(new Date()),
      amount: '',
      payment_type: 'rent',
      payment_method: 'cash',
      payment_month: new Date().getMonth() + 1,
      payment_year: new Date().getFullYear(),
      notes: ''
    });
    setSelectedRoomBill(null);
    setEditingPayment(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRoomBill(null);
    setEditingPayment(null);
  };

  const openEditModal = (payment) => {
    setEditingPayment(payment);
    setFormData({
      room_id: payment.room_id ? String(payment.room_id) : '',
      payment_date: formatDateForInput(payment.payment_date || new Date()),
      amount: payment.amount ? String(payment.amount) : '',
      payment_type: payment.payment_type || 'rent',
      payment_method: payment.payment_method || 'cash',
      payment_month: payment.bill_month || payment.payment_month || (new Date().getMonth() + 1),
      payment_year: payment.bill_year || payment.payment_year || new Date().getFullYear(),
      bill_month: payment.bill_month || payment.payment_month || '',
      bill_year: payment.bill_year || payment.payment_year || '',
      reference_number: payment.reference_number || '',
      notes: payment.notes || ''
    });
    // Try to fetch bill info for the selected room+period
    if (payment.room_id) fetchRoomBillForPayment(payment.room_id, payment.bill_month || payment.payment_month, payment.bill_year || payment.payment_year);
    setShowModal(true);
  };

  const handleRoomChange = (roomId) => {
    setFormData({...formData, room_id: roomId});
    if (roomId) {
      fetchRoomBillForPayment(roomId, formData.payment_month, formData.payment_year);
    } else {
      setSelectedRoomBill(null);
    }
  };

  const handlePeriodChange = (field, value) => {
    const newFormData = {...formData, [field]: value};
    setFormData(newFormData);
    
    if (newFormData.room_id && newFormData.payment_month && newFormData.payment_year) {
      fetchRoomBillForPayment(newFormData.room_id, newFormData.payment_month, newFormData.payment_year);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  const getPaymentTypeIcon = (type) => {
    switch (type) {
      case 'rent':
        return <Building className="w-4 h-4" />;
      case 'electricity':
        return <DollarSign className="w-4 h-4" />;
      default:
        return <CreditCard className="w-4 h-4" />;
    }
  };

  const getPaymentTypeColor = (type) => {
    switch (type) {
      case 'rent':
        return 'bg-blue-100 text-blue-800';
      case 'electricity':
        return 'bg-yellow-100 text-yellow-800';
      case 'deposit':
        return 'bg-green-100 text-green-800';
      case 'maintenance':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const downloadReceipt = async (payment) => {
    try {
      const response = await paymentsAPI.downloadReceipt(payment.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${payment.reference_number || payment.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt');
    }
  };

  const formatPDFDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const dayName = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName} ${day}-${month}-${year}`;
  };

  const handleDownloadFilteredPDF = () => {
    if (payments.length === 0) {
      toast.error('No payments to download');
      return;
    }

    // Create filter description for title
    let filterDesc = 'All Payments';
    if (filters.room_id) {
      const room = rooms.find(r => r.id === parseInt(filters.room_id));
      filterDesc = `Room ${room?.room_number || filters.room_id} Payments`;
    }
    if (filters.month && filters.year) {
      filterDesc += ` - ${getMonthName(parseInt(filters.month))} ${filters.year}`;
    } else if (filters.year) {
      filterDesc += ` - ${filters.year}`;
    }

    // Build HTML content
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${filterDesc}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            background: white;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
          }
          .header h1 {
            font-size: 24px;
            margin-bottom: 8px;
            color: #1a1a1a;
          }
          .header p {
            font-size: 13px;
            color: #555;
          }
          .summary {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-bottom: 25px;
            padding: 15px;
            background: #f5f5f5;
            border-radius: 6px;
          }
          .summary-item {
            text-align: center;
          }
          .summary-label {
            font-size: 11px;
            color: #666;
            margin-bottom: 5px;
          }
          .summary-value {
            font-size: 16px;
            font-weight: bold;
            color: #1a1a1a;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 11px;
          }
          thead {
            background: #333;
            color: white;
          }
          th {
            padding: 10px 6px;
            text-align: left;
            font-weight: 600;
            font-size: 11px;
          }
          td {
            padding: 8px 6px;
            border-bottom: 1px solid #e0e0e0;
          }
          tbody tr:nth-child(even) {
            background: #f9f9f9;
          }
          tbody tr:hover {
            background: #f0f0f0;
          }
          .amount {
            font-weight: 600;
            text-align: center;
          }
          .badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 3px;
            font-size: 10px;
            font-weight: 600;
          }
          .badge-rent {
            background: #dbeafe;
            color: #1e40af;
          }
          .badge-electricity {
            background: #fef3c7;
            color: #92400e;
          }
          .badge-both {
            background: #d1fae5;
            color: #065f46;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          .print-button {
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            background: #2563eb;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 600;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .print-button:hover {
            background: #1d4ed8;
          }
          @media print {
            .print-button {
              display: none;
            }
            body {
              padding: 0;
            }
            @page {
              size: A4;
              margin: 15mm;
            }
          }
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
        
        <div class="header">
          <h1>Payment Report</h1>
          <p>${filterDesc}</p>
          <p style="font-size: 11px; margin-top: 5px;">Generated on ${formatPDFDate(new Date().toISOString())}</p>
        </div>

        <div class="summary">
          <div class="summary-item">
            <div class="summary-label">Total Payments</div>
            <div class="summary-value">${payments.length}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Total Amount</div>
            <div class="summary-value">${formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0))}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Average Payment</div>
            <div class="summary-value">${formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0) / payments.length)}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Room</th>
              <th>Tenant</th>
              <th>Type</th>
              <th>Period</th>
              <th style="text-align: center;">Amount</th>
              <th>Method</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            ${payments.map(payment => `
              <tr>
                <td>${formatPDFDate(payment.payment_date)}</td>
                <td><strong>${payment.room_number}</strong></td>
                <td>${payment.tenant_name || 'Vacant'}</td>
                <td>
                  <span class="badge badge-${payment.payment_type}">
                    ${payment.payment_type.charAt(0).toUpperCase() + payment.payment_type.slice(1)}
                  </span>
                </td>
                <td>${getMonthName(payment.bill_month)} ${payment.bill_year}</td>
                <td class="amount">${formatCurrency(payment.amount)}</td>
                <td>${payment.payment_method?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}</td>
                <td>${payment.notes || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Payment Report - Generated by Tenant Bills Management System</p>
          <p>This is a computer-generated document</p>
        </div>
      </body>
      </html>
    `;

    // Open in new window
    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    
    // Set title after a brief delay to ensure it's applied
    setTimeout(() => {
      newWindow.document.title = filterDesc;
    }, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Payment Management</h1>
          <p className="text-secondary-600 dark:text-secondary-300">Record and track rent and electricity payments</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-secondary-100 dark:bg-secondary-800 rounded-xl p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-secondary-700 shadow-sm text-blue-600 dark:text-blue-400'
                  : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-100'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-secondary-700 shadow-sm text-blue-600 dark:text-blue-400'
                  : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-100'
              }`}
            >
              <Grid className="w-4 h-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'chart'
                  ? 'bg-white dark:bg-secondary-700 shadow-sm text-blue-600 dark:text-blue-400'
                  : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-100'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Charts</span>
            </button>
          </div>

          <button
            onClick={handleDownloadFilteredPDF}
            className="btn-outline flex items-center gap-2"
            title="Download Payment Report as PDF"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Download PDF</span>
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={openModal}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold text-sm shadow-sm transition-all"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Record Payment</span>
            <span className="sm:hidden">Add</span>
          </motion.button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Collected</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                {formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0))}
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Transactions</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                {payments.length}
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Building className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Rooms with Payments</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                {new Set(payments.map(p => p.room_id)).size}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex items-center justify-center flex-shrink-0">
            <Filter className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 flex-1">
            <div>
              <label className="block text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1">Room</label>
              <select
                name="room_id"
                value={filters.room_id}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                <option value="">All Rooms</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>
                    Room {room.room_number}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1">Year</label>
              <select
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                {generateYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1">Month</label>
              <select
                name="month"
                value={filters.month}
                onChange={handleFilterChange}
                className="form-input text-sm"
              >
                <option value="">All Months</option>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>
                    {getMonthName(month)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ room_id: '', payment_type: '', year: new Date().getFullYear(), month: '' })}
                className="btn-outline w-full text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Payments Display */}
      {payments.length > 0 ? (
        <>
          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">Payment Records</h3>
                    <p className="text-blue-100 text-xs">{payments.length} payment{payments.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-secondary-50 dark:bg-secondary-700/50 border-b border-secondary-200 dark:border-secondary-600">
                      <th onClick={() => setSortConfig(prev => ({ key: 'date', direction: prev.key === 'date' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Date {renderSortIcon('date')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'room', direction: prev.key === 'room' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Room {renderSortIcon('room')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'tenant', direction: prev.key === 'tenant' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Tenant {renderSortIcon('tenant')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'type', direction: prev.key === 'type' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Type {renderSortIcon('type')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'period', direction: prev.key === 'period' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Period {renderSortIcon('period')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'amount', direction: prev.key === 'amount' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-right px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Amount {renderSortIcon('amount')}</th>
                      <th onClick={() => setSortConfig(prev => ({ key: 'method', direction: prev.key === 'method' && prev.direction === 'asc' ? 'desc' : 'asc' }))}
                        className="cursor-pointer text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Method {renderSortIcon('method')}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Reference</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700">
                    {sortedPayments.map((payment, index) => (
                      <motion.tr
                        key={payment.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="hover:bg-secondary-50 dark:hover:bg-secondary-700/30 transition-colors group"
                      >
                        <td className="px-4 py-3 text-sm text-secondary-700 dark:text-secondary-300">{formatDate(payment.payment_date, 'EEE dd-MMM-yyyy')}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center flex-shrink-0">
                              <Building className="w-3.5 h-3.5 text-white" />
                            </div>
                            <span className="font-semibold text-secondary-900 dark:text-secondary-100 text-sm">{payment.room_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-300">
                            <User className="w-3.5 h-3.5 text-secondary-400 flex-shrink-0" />
                            <span>{payment.tenant_name || 'Vacant'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            payment.payment_type === 'rent'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : payment.payment_type === 'electricity'
                              ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : payment.payment_type === 'both'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-300'
                          }`}>
                            <DollarSign className="w-3 h-3" />
                            <span className="capitalize">{payment.payment_type || 'N/A'}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-secondary-600 dark:text-secondary-300">
                          {payment.bill_month && payment.bill_year
                            ? `${getMonthName(payment.bill_month)} ${payment.bill_year}`
                            : <span className="text-secondary-400">—</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-emerald-600 dark:text-emerald-400 text-sm">
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300">
                            {paymentMethods.find(m => m.value === payment.payment_method)?.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-secondary-500 dark:text-secondary-400">
                          {payment.reference_number || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openEditModal(payment)}
                              className="p-1.5 text-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
                              title="Edit Payment"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => downloadReceipt(payment)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Download Receipt"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            {payment.notes && (
                              <button
                                className="p-1.5 text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
                                title={payment.notes}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {payments.map((payment, index) => (
                <motion.div
                  key={payment.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.04 }}
                  className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className={`px-5 py-4 bg-gradient-to-r ${
                    payment.payment_type === 'rent'
                      ? 'from-blue-600 to-indigo-700'
                      : payment.payment_type === 'electricity'
                      ? 'from-amber-500 to-orange-600'
                      : payment.payment_type === 'both'
                      ? 'from-emerald-600 to-teal-700'
                      : 'from-secondary-500 to-secondary-700'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-white/80 text-xs capitalize">{payment.payment_type}</p>
                          <p className="text-white font-bold text-lg leading-tight">{formatCurrency(payment.amount)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white/70 text-xs">Room</p>
                        <p className="text-white font-bold text-base">{payment.room_number}</p>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                      <span className="font-medium text-secondary-900 dark:text-secondary-100">{payment.tenant_name || 'Vacant'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-300">
                      <Calendar className="w-4 h-4 text-secondary-400 flex-shrink-0" />
                      <span>{formatDate(payment.payment_date, 'EEE dd-MMM-yyyy')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-3">
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-0.5">Period</p>
                        <p className="text-sm font-semibold text-secondary-800 dark:text-secondary-200">
                          {payment.bill_month && payment.bill_year
                            ? `${getMonthName(payment.bill_month)} ${payment.bill_year}`
                            : '—'
                          }
                        </p>
                      </div>
                      <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-3">
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-0.5">Method</p>
                        <p className="text-sm font-semibold text-secondary-800 dark:text-secondary-200">
                          {paymentMethods.find(m => m.value === payment.payment_method)?.label || '—'}
                        </p>
                      </div>
                    </div>
                    {payment.reference_number && (
                      <div className="flex items-center justify-between text-xs border-t border-secondary-100 dark:border-secondary-700 pt-2">
                        <span className="text-secondary-500 dark:text-secondary-400">Ref</span>
                        <span className="font-mono text-secondary-700 dark:text-secondary-300">{payment.reference_number}</span>
                      </div>
                    )}
                    {payment.notes && (
                      <div className="bg-secondary-50 dark:bg-secondary-700/40 rounded-xl p-3 text-xs text-secondary-600 dark:text-secondary-300">
                        {payment.notes}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(payment)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-secondary-200 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors mt-2"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => downloadReceipt(payment)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-secondary-200 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors mt-2"
                      >
                        <Download className="w-4 h-4" />
                        Download Receipt
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Chart View */}
          {viewMode === 'chart' && (
            <div className="space-y-6">
              {/* KPI row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center flex-shrink-0">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Rooms Paying</p>
                    <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">{new Set(payments.map(p => p.room_id)).size}</p>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total Collected</p>
                    <p className="text-xl font-bold text-emerald-600">{formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0))}</p>
                  </div>
                </div>
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Avg Payment</p>
                    <p className="text-xl font-bold text-secondary-900 dark:text-secondary-100">{formatCurrency(payments.reduce((sum, p) => sum + parseFloat(p.amount), 0) / payments.length)}</p>
                  </div>
                </div>
              </div>

              {/* Payments by Room Chart */}
              <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-white font-semibold text-sm">Payments by Room</h3>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart
                      data={Object.entries(
                        payments.reduce((acc, payment) => {
                          const room = payment.room_number;
                          if (!acc[room]) acc[room] = 0;
                          acc[room] += parseFloat(payment.amount);
                          return acc;
                        }, {})
                      ).map(([room, amount]) => ({ room, amount }))}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="room" tick={{ fontSize: 12, fill: '#6b7280' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                      />
                      <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Payments by Type + Method */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Payments by Type</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={Object.entries(
                            payments.reduce((acc, payment) => {
                              const type = payment.payment_type?.charAt(0).toUpperCase() + payment.payment_type?.slice(1) || 'Unknown';
                              if (!acc[type]) acc[type] = 0;
                              acc[type] += parseFloat(payment.amount);
                              return acc;
                            }, {})
                          ).map(([name, value]) => ({ name, value }))}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          dataKey="value"
                        >
                          {Object.keys(
                            payments.reduce((acc, payment) => {
                              const type = payment.payment_type?.charAt(0).toUpperCase() + payment.payment_type?.slice(1) || 'Unknown';
                              acc[type] = true;
                              return acc;
                            }, {})
                          ).map((name, index) => (
                            <Cell key={`cell-${index}`} fill={
                              name === 'Rent' ? '#3b82f6' :
                              name === 'Electricity' ? '#f59e0b' :
                              name === 'Both' ? '#10b981' :
                              '#6366f1'
                            } />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden">
                  <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">Payments by Method</h3>
                  </div>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={Object.entries(
                          payments.reduce((acc, payment) => {
                            const method = paymentMethods.find(m => m.value === payment.payment_method)?.label || 'Unknown';
                            if (!acc[method]) acc[method] = 0;
                            acc[method] += parseFloat(payment.amount);
                            return acc;
                          }, {})
                        ).map(([method, amount]) => ({ method, amount }))}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={(v) => `$${v}`} />
                        <YAxis dataKey="method" type="category" width={120} tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'rgba(255,255,255,0.95)', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                        />
                        <Bar dataKey="amount" fill="#6366f1" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-sm border border-secondary-200 dark:border-secondary-700">
          <div className="text-center py-16 px-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <CreditCard className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-2">No payments found</h3>
            <p className="text-secondary-500 dark:text-secondary-400 mb-6">Start by recording your first payment</p>
            <button
              onClick={openModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:from-blue-700 hover:to-indigo-800 transition-all"
            >
              <Plus className="w-4 h-4" />
              Record First Payment
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="bg-white dark:bg-secondary-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col border border-secondary-200 dark:border-secondary-700"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gradient Header */}
              <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-lg font-bold text-white">{editingPayment ? 'Edit Payment' : 'Record New Payment'}</h2>
                </div>
                <button
                  onClick={closeModal}
                  className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>

              {/* Scrollable Form Body */}
              <div className="overflow-y-auto flex-1 p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Room</label>
                    <select
                      name="room_id"
                      value={formData.room_id}
                      onChange={(e) => handleRoomChange(e.target.value)}
                      className="form-input"
                      required
                    >
                      <option value="">Select a room</option>
                      {rooms.map(room => (
                        <option key={room.id} value={room.id}>
                          Room {room.room_number} - {room.tenant_name || 'Vacant'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Bill Period Selection */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Payment Month</label>
                      <select
                        name="payment_month"
                        value={formData.payment_month}
                        onChange={(e) => handlePeriodChange('payment_month', parseInt(e.target.value))}
                        className="form-input"
                        required
                      >
                        {Array.from({length: 12}, (_, i) => (
                          <option key={i + 1} value={i + 1}>
                            {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Payment Year</label>
                      <select
                        name="payment_year"
                        value={formData.payment_year}
                        onChange={(e) => handlePeriodChange('payment_year', parseInt(e.target.value))}
                        className="form-input"
                        required
                      >
                        {Array.from({length: 5}, (_, i) => {
                          const year = new Date().getFullYear() - 2 + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Bill Information Display */}
                  {selectedRoomBill && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                      <h3 className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-3">Monthly Bill Details</h3>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-blue-600 dark:text-blue-400">Rent Amount:</span>
                          <span className="text-blue-900 dark:text-blue-100 font-semibold">{formatCurrency(selectedRoomBill.rent_amount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-600 dark:text-blue-400">Electricity Amount:</span>
                          <span className="text-blue-900 dark:text-blue-100 font-semibold">{formatCurrency(selectedRoomBill.electricity_amount || 0)}</span>
                        </div>
                        <div className="flex justify-between border-t border-blue-200 dark:border-blue-700 pt-1.5">
                          <span className="text-blue-700 dark:text-blue-300 font-semibold">Total Due:</span>
                          <span className="text-blue-900 dark:text-blue-100 font-bold">{formatCurrency(selectedRoomBill.total_amount)}</span>
                        </div>
                        {selectedRoomBill.total_paid > 0 && (
                          <div className="flex justify-between">
                            <span className="text-blue-600 dark:text-blue-400">Already Paid:</span>
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{formatCurrency(selectedRoomBill.total_paid)}</span>
                          </div>
                        )}
                        {selectedRoomBill.outstanding > 0 && (
                          <div className="flex justify-between">
                            <span className="text-blue-600 dark:text-blue-400">Outstanding:</span>
                            <span className="text-red-500 dark:text-red-400 font-semibold">{formatCurrency(selectedRoomBill.outstanding)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Payment Type</label>
                      <select
                        name="payment_type"
                        value={formData.payment_type}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        <option value="rent">Rent</option>
                        <option value="electricity">Electricity</option>
                        <option value="both">Both (Rent + Electricity)</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Payment Method</label>
                      <select
                        name="payment_method"
                        value={formData.payment_method}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        {paymentMethods.map(method => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Payment Date</label>
                      <input
                        type="date"
                        name="payment_date"
                        value={formData.payment_date}
                        onChange={handleChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Amount</label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Bill Month</label>
                      <select
                        name="bill_month"
                        value={formData.bill_month}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">N/A</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                          <option key={month} value={month}>
                            {getMonthName(month)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Bill Year</label>
                      <select
                        name="bill_year"
                        value={formData.bill_year}
                        onChange={handleChange}
                        className="form-input"
                      >
                        <option value="">N/A</option>
                        {generateYears().map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Reference Number <span className="normal-case font-normal text-secondary-400">(Optional)</span></label>
                    <input
                      type="text"
                      name="reference_number"
                      value={formData.reference_number}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="e.g., TXN123456, CHECK001"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">Notes <span className="normal-case font-normal text-secondary-400">(Optional)</span></label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      className="form-input"
                      rows="3"
                      placeholder="Additional notes about this payment..."
                    />
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 rounded-xl border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 text-sm font-medium hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:from-blue-700 hover:to-indigo-800 transition-all"
                    >
                      {editingPayment ? 'Save Changes' : 'Record Payment'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentsPage;
